import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
    TextField,
    CircularProgress,
    Chip,
    Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import unclaimedPackageService from "../../services/unclaimedPackageService";

// One row per line — each line is a tracking number, optionally followed by
// its own D/R (tab, comma, or whitespace-separated: "1Z... 1832741" or
// "1Z...\t1832741"), since unlike carrier/description/notes below, the dock
// receipt genuinely varies per package. A line with just a tracking number
// (no D/R yet) is still valid. Duplicate tracking numbers keep the first
// occurrence's D/R.
const parseRows = (raw) => {
    const byTracking = new Map();
    raw
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => {
            const parts = line
                .split(/\t|,/)
                .map((p) => p.trim())
                .filter(Boolean);
            const [trackingNumber, dockReceiptNumber] =
                parts.length >= 2 ? parts : line.split(/\s+/).filter(Boolean);
            if (!trackingNumber || byTracking.has(trackingNumber)) return;
            byTracking.set(trackingNumber, dockReceiptNumber || "");
        });
    return [...byTracking.entries()].map(([trackingNumber, dockReceiptNumber]) => ({
        trackingNumber,
        dockReceiptNumber,
    }));
};

const BulkAddUnclaimedPackagesDialog = ({ open, onClose, onCreated }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [raw, setRaw] = useState("");
    const [carrier, setCarrier] = useState("");
    const [description, setDescription] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setRaw("");
            setCarrier("");
            setDescription("");
            setNotes("");
        }
    }, [open]);

    const rows = parseRows(raw);

    const handleSave = async () => {
        if (!rows.length) {
            enqueueSnackbar("Enter at least one tracking number", { variant: "error" });
            return;
        }
        setSaving(true);
        try {
            const { data } = await unclaimedPackageService.bulkCreate({
                rows,
                carrier: carrier || undefined,
                description: description || undefined,
                notes: notes || undefined,
            });
            enqueueSnackbar(data.message || `${data.data.createdCount} package(s) logged`, {
                variant: data.data.createdCount > 0 ? "success" : "warning",
            });
            onCreated?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to bulk-add packages", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontWeight={700}>Bulk Add Unclaimed Packages</Typography>
                <IconButton size="small" onClick={onClose}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <TextField
                    label="Tracking Numbers *"
                    placeholder={"1Z999AA10123456784\t1832741\n1Z999AA10123456785, 1832742\n1Z999AA10123456786"}
                    fullWidth
                    multiline
                    minRows={6}
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    helperText="One per line. Optionally add that package's D/R after the tracking number (tab, comma, or space-separated) — leave it off if not known yet. Duplicates and already-logged tracking numbers are skipped automatically."
                    sx={{ mb: 2, "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                />

                {rows.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                            {rows.length} tracking number{rows.length === 1 ? "" : "s"} detected
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 100, overflowY: "auto" }}>
                            {rows.map((r) => (
                                <Chip
                                    key={r.trackingNumber}
                                    label={r.dockReceiptNumber ? `${r.trackingNumber} (D/R ${r.dockReceiptNumber})` : r.trackingNumber}
                                    size="small"
                                    sx={{ fontFamily: "monospace" }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    These fields, if set, are applied to every package in this batch.
                </Alert>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField label="Carrier" fullWidth value={carrier} onChange={(e) => setCarrier(e.target.value)} />
                    <TextField label="Description" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
                    <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        minRows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || !rows.length}>
                    {saving ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        `Log ${rows.length || ""} Package${rows.length === 1 ? "" : "s"}`
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAddUnclaimedPackagesDialog;
